import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Search, Loader2, AlertCircle, Settings, Info, ChevronLeft, ChevronRight, ListChecks, X, Download, Sun, Moon, Monitor, Lightbulb } from "lucide-react";
import PropertyModal from 'PropertyModal';
import HelpModal from 'HelpModal';
import BulkSearchModal from 'BulkSearchModal';
import SearchHighlightOverlay from 'SearchHighlightOverlay';

const SearchApp = () => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  // A colon anywhere in the trimmed query means syntax mode — same rule the backend uses to route /api/search.
  const isSyntaxMode = query.trim().includes(':');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // True when `error` is a query-parse hint from the compiler rather than a
  // hard failure — rendered differently, and results are kept on screen.
  const [errorIsSyntaxHint, setErrorIsSyntaxHint] = useState(false);

  const [themePref, setThemePref] = useState(() => localStorage.getItem('theme') || 'system');
  const [systemDark, setSystemDark] = useState(window.matchMedia('(prefers-color-scheme: dark)').matches);
  const isDark = themePref === 'system' ? systemDark : themePref === 'dark';

  const pickTheme = (next) => {
    setThemePref(next);
    if (next === 'system') localStorage.removeItem('theme');
    else localStorage.setItem('theme', next);
  };

  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [allProperties, setAllProperties] = useState([]);
  const columnTokens = useMemo(
    () => allProperties.map((prop) => prop.token).filter(Boolean),
    [allProperties]
  );
  const searchInputRef = useRef(null);
  const searchOverlayRef = useRef(null);
  const [searchProperties, setSearchProperties] = useState([]);
  const [propertyLabels, setPropertyLabels] = useState({});

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const [currentSearched, setCurrentSearched] = useState('');  // Track the query we've actually searched for
  
  const [userName, setUserName] = useState('');
  const [authReady, setAuthReady] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [cachedResults, setCachedResults] = useState({});  // Cache object: { queryKey: { contacts: [], total: 0 } }
  const pageSize = 10;

  const getCacheKey = (query, pageNum, props) => `${query}_${pageNum}_${props.join(',')}`;


  const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || '';

  // Pick up ?token= from URL after OAuth callback, persist to localStorage.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      localStorage.setItem('auth_token', token);
      params.delete('token');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const getToken = () => localStorage.getItem('auth_token');

  const authFetch = (url, options = {}) => {
    const token = getToken();
    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
  };

  const redirectToLogin = () => {
    window.location.href = `${AUTH_SERVICE_URL}/login?redirect_url=${encodeURIComponent(window.location.href)}`;
  };

  // Fetch user info when component mounts
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await authFetch('/api/user');
        if (response.status === 401 || response.status === 403) { redirectToLogin(); return; }
        if (response.ok) {
          const data = await response.json();
          setUserName(data.name);
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
      setAuthReady(true);
    };
    fetchUserInfo();
  }, []);

  // Fetch searchable-column metadata once — drives both the table headers
  // (all properties, so labels resolve regardless of current selection) and
  // the initial search selection (whichever properties come back as default).
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await authFetch('/api/properties');
        if (response.ok) {
          const data = await response.json();
          setAllProperties(data);
          setSearchProperties(data.filter(prop => prop.default).map(prop => prop.name));
          const labels = {};
          data.forEach(prop => {
            labels[prop.name] = prop.label;
          });
          setPropertyLabels(labels);
        } else {
          console.error('Failed to fetch properties');
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    fetchProperties();
  }, []);

  // Track the system color-scheme preference so we can follow it when themePref === 'system'.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  // Implement debouncing with useEffect
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 500);
    return () => clearTimeout(timer);
  }, [query]);

  // Separate effect for performing the search
  useEffect(() => {
    const performSearch = async () => {
      const debouncedIsSyntaxMode = debouncedQuery.includes(':');
      if (!debouncedQuery || (debouncedQuery.length < 3 && !debouncedIsSyntaxMode)) {
        setResults([]);
        setError(null);
        setErrorIsSyntaxHint(false);
        setTotal(0);
        return;
      }

      const cacheKey = getCacheKey(debouncedQuery, page, searchProperties);

      // Check cache first
      if (cachedResults[cacheKey]) {
        setResults(cachedResults[cacheKey].contacts);
        setTotal(cachedResults[cacheKey].total);
        return;
      }

      setLoading(true);
      setError(null);
      setErrorIsSyntaxHint(false);

      try {
        // Now using relative URL since we're serving from same origin
        const offset = (page - 1) * pageSize;
        const response = await authFetch(
          `/api/search?q=${encodeURIComponent(debouncedQuery)}&properties=${searchProperties.join(',')}&offset=${offset}&limit=${pageSize}`
        );
        
        if (response.status === 401 || response.status === 403) { redirectToLogin(); return; }
        if (response.status === 400) {
          // The compiler rejects an unparseable query with {error: message}.
          // That's a normal state while typing, not a failure: surface the
          // server's message as a hint and leave previous results on screen.
          let parseMessage = null;
          try {
            const body = await response.json();
            if (body && typeof body.error === 'string' && body.error) parseMessage = body.error;
          } catch {
            // Body wasn't JSON — treat like any other non-OK response below.
          }
          if (parseMessage) {
            setError(parseMessage);
            setErrorIsSyntaxHint(true);
            return;
          }
        }
        if (!response.ok) {
          throw new Error(response.status === 429
            ? 'Searching too fast, please wait...'
            : 'Failed to fetch results'
          );
        }

        const data = await response.json();
        // Tolerate a response without contacts rather than storing undefined —
        // results.length is read during render, so that blanks the whole app.
        const contacts = data.contacts || [];
        setResults(contacts);
        setTotal(data.total || 0);
        setCurrentSearched(debouncedQuery);
        // Cache the results
        setCachedResults(prev => ({
          ...prev,
          [cacheKey]: { contacts, total: data.total || 0 }
        }));
      } catch (err) {
        setError(err.message);
        setErrorIsSyntaxHint(false);
        setResults([]);
        setCurrentSearched(debouncedQuery);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery, searchProperties, page]);

  // Reset page when query changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  // Keep the highlight overlay's horizontal scroll pinned to the real
  // input's so long queries stay pixel-aligned while scrolling.
  useEffect(() => {
    if (searchInputRef.current && searchOverlayRef.current) {
      searchOverlayRef.current.scrollLeft = searchInputRef.current.scrollLeft;
    }
  }, [query]);

  const totalPages = Math.ceil(total / pageSize);

  // Add an effect just to log searchProperties changes
  /* useEffect(() => {
    console.log('searchProperties changed to:', searchProperties);
  }, [searchProperties]); */

  const [exporting, setExporting] = useState(null);  // 'csv' | 'xlsx' | null
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);

  useEffect(() => {
    if (!exportMenuOpen && !themeMenuOpen) return;
    const onDocClick = (e) => {
      if (exportMenuOpen && !e.target.closest('[data-export-menu]')) setExportMenuOpen(false);
      if (themeMenuOpen && !e.target.closest('[data-theme-menu]')) setThemeMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [exportMenuOpen, themeMenuOpen]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      let columns;
      let rows;
      let filename;

      if (bulkResults) {
        columns = [
          { key: '_status', label: 'Status' },
          { key: '_term', label: 'Search Term' },
          { key: '_termType', label: 'Term Type' },
          { key: '_reason', label: 'Reason' },
          ...searchProperties.map(p => ({ key: p, label: propertyLabels[p] || p })),
        ];
        rows = [
          ...bulkResults.found.map(r => ({ ...r, _status: 'Found' })),
          ...bulkResults.notFound.map(r => ({ ...r, _status: 'Not Found' })),
          ...(bulkResults.errors || []).map(r => ({ ...r, _status: 'Error' })),
        ];
        filename = 'alumni-archive-bulk';
      } else {
        columns = searchProperties.map(p => ({ key: p, label: propertyLabels[p] || p }));
        rows = results;
        filename = currentSearched ? `alumni-archive-${currentSearched}` : 'alumni-archive';
      }

      if (rows.length === 0) {
        setError('Nothing to export.');
        setErrorIsSyntaxHint(false);
        return;
      }

      const resp = await authFetch('/api/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format, filename, columns, rows }),
      });

      if (resp.status === 401 || resp.status === 403) { redirectToLogin(); return; }
      if (!resp.ok) {
        let detail = '';
        try {
          const json = await resp.json();
          detail = json?.detail || JSON.stringify(json);
        } catch (_) {
          detail = await resp.text();
        }
        throw new Error(`Export failed (${resp.status})` + (detail ? `: ${detail}` : ''));
      };

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed');
      setErrorIsSyntaxHint(false);
    } finally {
      setExporting(null);
    }
  };

  const canExport = (bulkResults && (bulkResults.found.length + bulkResults.notFound.length) > 0) || results.length > 0;

  // Save and Search from the modal: commits the checkbox selection as the
  // display columns and drops the query it composed into the search bar.
  const handleModalSearch = (composedQuery, newProperties) => {
    setSearchProperties(newProperties);
    setQuery(composedQuery);
    setDebouncedQuery(composedQuery.trim());
    setIsPropertyModalOpen(false);
  };

  // Drag-to-reorder the display columns. Reordering searchProperties reorders
  // both the header cells and every row's cells (they map over the same array),
  // and export follows the same order. It only changes column *order*, not which
  // columns are searched, so results are unchanged (a reorder does re-run the
  // search once — the cache key includes column order). The fixed HubSpot column
  // isn't part of searchProperties, so it never moves.
  const dragColIndex = useRef(null);
  const [dragOverColIndex, setDragOverColIndex] = useState(null);

  const moveColumn = (from, to) => {
    if (from === null || to === null || from === to) return;
    setSearchProperties(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const columnDragProps = index => ({
    // Whole header is the reorder grab target. The resize handle is a separate
    // child that swallows its own pointer gesture (see startResize), so grabbing
    // the header body reorders and grabbing the edge resizes — independently.
    draggable: true,
    onDragStart: e => { dragColIndex.current = index; },
    onDragOver: e => { e.preventDefault(); if (dragOverColIndex !== index) setDragOverColIndex(index); },
    onDragLeave: () => { if (dragOverColIndex === index) setDragOverColIndex(null); },
    onDrop: e => { e.preventDefault(); moveColumn(dragColIndex.current, index); dragColIndex.current = null; setDragOverColIndex(null); },
    onDragEnd: () => { dragColIndex.current = null; setDragOverColIndex(null); },
  });

  // Column resizing (main results table). The table is fixed-layout with a
  // colgroup of pixel widths; dragging a border transfers width between the two
  // adjacent columns so their sum — and thus the whole table width — never
  // changes. Each column can't shrink below the width of its own header label.
  const HUBSPOT_COL_WIDTH = 44; // px — fixed, non-resizable leading logo column
  const [columnWidths, setColumnWidths] = useState({}); // prop name -> px
  const [minColumnWidths, setMinColumnWidths] = useState({}); // prop name -> px (label width)
  const tableWrapRef = useRef(null);
  const headerLabelRefs = useRef({}); // prop name -> label span element
  const resizeRef = useRef(null);
  const columnSetSig = useRef('');
  const fittedRef = useRef(false);

  // Measure header labels (the per-column minimum) and fit column widths to the
  // container. A ResizeObserver drives this so the first fit waits until the
  // wrapper actually has a width, and window resizes are handled. We only
  // (re)distribute when the column *set* changes or widths are missing — a pure
  // reorder, a manual resize, or a window resize preserve existing widths.
  useLayoutEffect(() => {
    const wrap = tableWrapRef.current;
    if (!wrap || !searchProperties.length) return;

    const fit = () => {
      const mins = {};
      searchProperties.forEach(prop => {
        const el = headerLabelRefs.current[prop];
        // scrollWidth is the label's full text width even if the cell clips it;
        // +48 covers the th's px-6 padding, +16 for the resize guide/handle.
        mins[prop] = (el ? Math.ceil(el.scrollWidth) : 40) + 48 + 16;
      });
      setMinColumnWidths(mins);

      const sig = [...searchProperties].sort().join(',');
      const setChanged = sig !== columnSetSig.current;
      const hasAll = searchProperties.every(p => columnWidths[p] != null);
      if (!setChanged && hasAll && fittedRef.current) return; // keep widths
      const total = wrap.clientWidth;
      if (!total) return; // not laid out yet — the observer will call us again

      columnSetSig.current = sig;
      fittedRef.current = true;
      const avail = Math.max(0, total - HUBSPOT_COL_WIDTH);
      const sumMin = searchProperties.reduce((s, p) => s + mins[p], 0);
      const widths = {};
      if (avail <= sumMin) {
        searchProperties.forEach(p => { widths[p] = mins[p]; });
      } else {
        const extra = Math.floor((avail - sumMin) / searchProperties.length);
        searchProperties.forEach(p => { widths[p] = mins[p] + extra; });
      }
      setColumnWidths(widths);
    };

    fit();
    const ro = new ResizeObserver(() => fit());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [searchProperties, propertyLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resize is a pointer gesture on the handle only; window-level pointer
  // listeners track the drag so it keeps working past the thin handle, and
  // always tear down on pointerup. It's independent of the header's HTML5 drag
  // (the handle stops the gesture from bubbling), so reorder is unaffected.
  const onResizeMove = useCallback(e => {
    const r = resizeRef.current;
    if (!r) return;
    let delta = e.clientX - r.startX;
    delta = Math.max(-(r.curW - r.minCur), Math.min(r.nextW - r.minNext, delta));
    setColumnWidths(prev => ({ ...prev, [r.cur]: r.curW + delta, [r.next]: r.nextW - delta }));
  }, []);

  const onResizeEnd = useCallback(() => {
    resizeRef.current = null;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    window.removeEventListener('pointermove', onResizeMove);
    window.removeEventListener('pointerup', onResizeEnd);
  }, [onResizeMove]);

  const startResize = index => e => {
    const cur = searchProperties[index];
    const next = searchProperties[index + 1];
    if (!next) return; // last column has no right neighbour to trade width with
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      cur, next,
      startX: e.clientX,
      curW: columnWidths[cur],
      nextW: columnWidths[next],
      minCur: minColumnWidths[cur] || 40,
      minNext: minColumnWidths[next] || 40,
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', onResizeMove);
    window.addEventListener('pointerup', onResizeEnd);
  };

  const tableTotalWidth = useMemo(() => {
    if (!searchProperties.every(p => columnWidths[p] != null)) return undefined;
    return HUBSPOT_COL_WIDTH + searchProperties.reduce((s, p) => s + columnWidths[p], 0);
  }, [searchProperties, columnWidths]);

  // Contacts synced to HubSpot get a logo link to their CRM record. It's not a
  // configurable/searchable display column — it fills a fixed narrow leading
  // column (index 0, before the configurable columns), empty for contacts with
  // no hubspot_record_id.
  const renderHubspotBadge = hubspotRecordId => {
    if (!hubspotRecordId) return null;
    return (
      <a
        href={`https://app-eu1.hubspot.com/contacts/25601993/record/0-1/${hubspotRecordId}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        title="View in HubSpot"
        className="inline-flex shrink-0 align-middle"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#FF7A59" aria-label="HubSpot" role="img">
          <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.851-6.112a2.508 2.508 0 10-1.177 1.531l-.011-.006 7.736 6.021a6.253 6.253 0 00-1.05 3.484c0 1.331.416 2.564 1.126 3.578l-.013-.019-2.352 2.352a1.833 1.833 0 00-.585-.104h-.001a2.028 2.028 0 102.028 2.028 1.821 1.821 0 00-.104-.585l.005.015 2.325-2.325a6.27 6.27 0 104.784-11.09l-.032-.007zm-1.063 9.418a3.217 3.217 0 110-6.434 3.217 3.217 0 010 6.434z" />
        </svg>
      </a>
    );
  };

  const renderCellValue = (prop, value) => {
    if (!value) return '';
    if (prop === 'linkedin_url') {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className={`underline ${isDark ? 'text-brand-400 hover:text-brand-300' : 'text-brand-600 hover:text-brand-700'}`}
        >
          {value}
        </a>
      );
    }
    return value;
  };

  if (!authReady) return (
    <div className={`min-h-screen w-full flex flex-col items-center justify-center ${isDark ? 'dark bg-night' : 'bg-brand-50'}`}>
      <img
        src="https://storage.googleapis.com/generic-public-bucket/amuta_8200.png"
        alt="8200 Alumni Association"
        className="h-40 mb-8"
      />
      <Loader2 className="h-8 w-8 text-brand-500 animate-spin" />
    </div>
  );

  return (
    <div className={`min-h-screen w-full ${isDark ? 'dark bg-night' : 'bg-brand-50'}`}>
      <div className="w-full px-4 pt-16">

        {/* Brand Section — association logo + Archive wordmark */}
        <div className="flex items-center justify-center gap-4 mb-2">
          <img
            src="https://storage.googleapis.com/generic-public-bucket/amuta_8200.png"
            alt="8200 Alumni Association"
            className="h-24" // Association identity, smaller than alumni-search's hero logo
          />
          <div className={`h-12 w-px ${isDark ? 'bg-night-header' : 'bg-brand-300'}`} />
          <div className="text-left">
            <h1 className={`text-3xl font-bold tracking-tight leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Alumni <span className={isDark ? 'text-brand-400' : 'text-brand-600'}>Archive</span>
            </h1>
            <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              The community's record, searchable
            </p>
          </div>
        </div>

        {/* Main content container */}
        <div className="max-w-7xl mx-auto">
          {/* User Greeting - same width as search container */}
          {userName && (
            <div className="max-w-7xl mx-auto mb-2">
              <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Welcome back, {userName.split(' ')[0]}! 👋
              </p>
            </div>
          )}

          {/* Search section with constrained width */}
          <div className="max-w-7xl mx-auto relative mt-4"> {/* Same width as the results table below */}
            {/* Help and Settings buttons */}
            <div className="mb-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1" />
              <div className="flex items-center gap-4 flex-wrap justify-center">

              {/* Bulk Search Button */}
              <button
                onClick={() => setIsBulkModalOpen(true)}
                className={`flex items-center gap-1
                  ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <ListChecks size={16} />
                <span className="text-sm">Bulk</span>
              </button>

              {/* Export menu */}
              <div className="relative" data-export-menu>
                <button
                  onClick={() => canExport && setExportMenuOpen(o => !o)}
                  disabled={!canExport || exporting !== null}
                  title={canExport ? 'Export current view' : 'Nothing to export'}
                  aria-haspopup="menu"
                  aria-expanded={exportMenuOpen}
                  className={`flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed
                    ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {exporting !== null ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  <span className="text-sm">Export</span>
                </button>
                {exportMenuOpen && (
                  <div
                    role="menu"
                    className={`absolute right-0 mt-2 min-w-[8rem] rounded-md shadow-lg ring-1 z-20
                      ${isDark
                        ? 'bg-night-raised ring-brand-900 text-brand-100'
                        : 'bg-white ring-brand-200 text-brand-900'}`}
                  >
                    {['csv', 'xlsx'].map(fmt => (
                      <button
                        key={fmt}
                        role="menuitem"
                        onClick={() => { setExportMenuOpen(false); handleExport(fmt); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2
                          ${isDark ? 'hover:bg-night-header' : 'hover:bg-brand-50'}`}
                      >
                        <Download size={14} />
                        {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Help Button */}
              <button
                onClick={() => setIsHelpOpen(true)}
                className={`flex items-center gap-1 
                  ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Info size={16} />
                <span className="text-sm">Help</span>
              </button>

              {/* Searchable Columns Button */}
              <button
                onClick={() => setIsPropertyModalOpen(true)}
                className={`flex items-center gap-1
                  ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Settings size={16} />
                <span className="text-sm">Search Columns</span>
              </button>
              </div>

              {/* Theme menu — right-aligned with the search bar's rounded edge */}
              <div className="flex-1 flex justify-end pr-6" data-theme-menu>
                <div className="relative">
                  <button
                    onClick={() => setThemeMenuOpen(o => !o)}
                    title={`Theme: ${themePref}`}
                    aria-haspopup="menu"
                    aria-expanded={themeMenuOpen}
                    className={`flex items-center gap-1
                      ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {themePref === 'system'
                      ? <Monitor size={16} />
                      : themePref === 'dark'
                        ? <Moon size={16} />
                        : <Sun size={16} />}
                    {/* <span className="text-sm">Theme</span> */}
                  </button>
                  {themeMenuOpen && (
                    <div
                      role="menu"
                      className={`absolute right-0 mt-2 min-w-[8rem] rounded-md shadow-lg ring-1 z-20
                        ${isDark
                          ? 'bg-night-raised ring-brand-900 text-brand-100'
                          : 'bg-white ring-brand-200 text-brand-900'}`}
                    >
                      {[
                        { key: 'system', label: 'System', Icon: Monitor },
                        { key: 'light', label: 'Light', Icon: Sun },
                        { key: 'dark', label: 'Dark', Icon: Moon },
                      ].map(({ key, label, Icon }) => (
                        <button
                          key={key}
                          role="menuitemradio"
                          aria-checked={themePref === key}
                          onClick={() => { setThemeMenuOpen(false); pickTheme(key); }}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2
                            ${themePref === key ? (isDark ? 'bg-night-header' : 'bg-brand-100') : ''}
                            ${isDark ? 'hover:bg-night-header' : 'hover:bg-brand-50'}`}
                        >
                          <Icon size={14} />
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onScroll={(e) => {
                  if (searchOverlayRef.current) searchOverlayRef.current.scrollLeft = e.target.scrollLeft;
                }}
                placeholder={isSyntaxMode ? 'e.g. email:gmail.com -first:john' : 'Start typing to search...'}
                autoFocus
                tabIndex={1}
                spellCheck={false}
                autoComplete="off"
                style={{ color: 'transparent', caretColor: isDark ? '#ffffff' : '#111827' }}
                className={`relative w-full pl-10 pr-4 py-3 rounded-full border-2
                  ${isDark
                    ? (isSyntaxMode
                        // Dark mode: syntax turns darker/sunken vs. the page background.
                        ? 'bg-[#0b0817] border-brand-900 placeholder-gray-500 focus:border-brand-700'
                        // Dark mode: free text (default) is the brighter panel.
                        : 'bg-night-header border-brand-600 placeholder-gray-400 focus:border-brand-500')
                    : (isSyntaxMode
                        // Light mode inverted: syntax turns brighter (crisp white).
                        ? 'bg-white border-brand-600 focus:border-brand-700'
                        // Light mode inverted: free text (default) is the darker/tinted panel.
                        : 'bg-brand-100 border-brand-300 focus:border-brand-500')}
                  focus:ring-2 focus:ring-brand-200 shadow-lg text-lg transition-colors duration-200`}
              />
              <SearchHighlightOverlay
                ref={searchOverlayRef}
                query={query}
                knownTokens={columnTokens}
                isDark={isDark}
                className="absolute inset-0 flex items-center pl-10 pr-4 py-3 border-2 border-transparent
                  text-lg leading-normal whitespace-pre overflow-hidden pointer-events-none select-none"
              />
              {loading && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <Loader2 className="h-5 w-5 text-brand-500 animate-spin" />
                </div>
              )}
            </div>

          </div>
        </div>

        {error && (
          <div className="max-w-7xl mx-auto"> {/* Error message aligned with search box */}
            <div className={`mt-4 p-4 rounded-lg flex items-center gap-2
              ${errorIsSyntaxHint
                ? (isDark ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-50 text-amber-800')
                : (isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-700')}`}>
              {errorIsSyntaxHint ? <Lightbulb className="h-5 w-5 shrink-0" /> : <AlertCircle className="h-5 w-5" />}
              {error}
            </div>
          </div>
        )}

        {/* Table section with full width */}
        <div className="max-w-7xl mx-auto">
          {bulkResults ? (
            <div className={`mt-6 rounded-lg shadow-lg overflow-hidden
              ${isDark ? 'bg-night-raised' : 'bg-white'}`}>
              <div className={`px-4 py-3 flex items-center justify-between border-b
                ${isDark ? 'border-brand-900 text-brand-200' : 'border-brand-100 text-brand-800'}`}>
                <span className="text-sm font-semibold">
                  Bulk results — Found: {bulkResults.found.length}, Not Found: {bulkResults.notFound.length}, Errors: {(bulkResults.errors || []).length}
                </span>
                <button
                  onClick={() => setBulkResults(null)}
                  className={`flex items-center gap-1 text-sm
                    ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <X size={16} /> Clear bulk results
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={isDark ? 'bg-night-header' : 'bg-brand-100'}>
                    <tr>
                      <th className={`w-8 px-3 py-3 ${isDark ? 'text-brand-200' : 'text-brand-900'}`}>
                        <span className="sr-only">HubSpot</span>
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                        ${isDark ? 'text-brand-200' : 'text-brand-900'}`}>
                        Search Term
                      </th>
                      {searchProperties.map((prop, i) => (
                        <th key={prop} {...columnDragProps(i)} title="Drag to reorder"
                          className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-move select-none
                          ${dragOverColIndex === i ? (isDark ? 'bg-brand-800' : 'bg-brand-200') : ''}
                          ${isDark ? 'text-brand-200' : 'text-brand-900'}`}>
                          {propertyLabels[prop] || prop}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {bulkResults.found.length > 0 && (
                      <tr className={isDark ? 'bg-night-header' : 'bg-brand-50'}>
                        <td colSpan={searchProperties.length + 2}
                          className={`px-6 py-2 text-xs font-semibold uppercase tracking-wider
                            ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                          Found ({bulkResults.found.length})
                        </td>
                      </tr>
                    )}
                    {bulkResults.found.map((row, idx) => (
                      <tr key={`f-${idx}`} className={`transition-colors duration-150
                        ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-900'}`}>
                        <td className="w-8 px-3 py-4 whitespace-nowrap text-sm">
                          {renderHubspotBadge(row.hubspot_record_id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                          {row._term}
                          <span className={`ml-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            ({row._termType})
                          </span>
                        </td>
                        {searchProperties.map(prop => (
                          <td key={prop} className="px-6 py-4 whitespace-nowrap text-sm">
                            {renderCellValue(prop, row[prop])}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {bulkResults.notFound.length > 0 && (
                      <tr className={isDark ? 'bg-night-header' : 'bg-brand-50'}>
                        <td colSpan={searchProperties.length + 2}
                          className={`px-6 py-2 text-xs font-semibold uppercase tracking-wider
                            ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                          Not Found ({bulkResults.notFound.length})
                        </td>
                      </tr>
                    )}
                    {bulkResults.notFound.map((row, idx) => (
                      <tr key={`nf-${idx}`}
                        className={isDark ? 'bg-amber-900/20 text-amber-200' : 'bg-amber-50 text-amber-900'}>
                        <td className="w-8 px-3 py-4" />
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                          {row._term}
                          <span className={`ml-2 text-xs ${isDark ? 'text-amber-300/70' : 'text-amber-700/70'}`}>
                            ({row._termType})
                          </span>
                        </td>
                        {searchProperties.map(prop => (
                          <td key={prop} className="px-6 py-4 whitespace-nowrap text-sm">—</td>
                        ))}
                      </tr>
                    ))}
                    {(bulkResults.errors || []).length > 0 && (
                      <tr className={isDark ? 'bg-night-header' : 'bg-brand-50'}>
                        <td colSpan={searchProperties.length + 2}
                          className={`px-6 py-2 text-xs font-semibold uppercase tracking-wider
                            ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                          Errors ({bulkResults.errors.length})
                        </td>
                      </tr>
                    )}
                    {(bulkResults.errors || []).map((row, idx) => (
                      <tr key={`e-${idx}`}
                        className={isDark ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-900'}>
                        <td className="w-8 px-3 py-4" />
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                          {row._term}
                          <span className={`ml-2 text-xs ${isDark ? 'text-red-300/70' : 'text-red-700/70'}`}>
                            ({row._termType})
                          </span>
                        </td>
                        {searchProperties.map((prop, i) => (
                          <td key={prop} className="px-6 py-4 whitespace-nowrap text-sm italic">
                            {i === 0 ? (row._reason || 'error') : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className={`mt-6 rounded-lg shadow-lg overflow-hidden
              ${isDark ? 'bg-night-raised' : 'bg-white'}`}>
              <div className="overflow-x-auto" ref={tableWrapRef}>
                <table className="divide-y divide-gray-200"
                  style={{ width: tableTotalWidth ? `${tableTotalWidth}px` : '100%', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: HUBSPOT_COL_WIDTH }} />
                    {searchProperties.map(prop => (
                      <col key={prop} style={columnWidths[prop] != null ? { width: columnWidths[prop] } : undefined} />
                    ))}
                  </colgroup>
                  <thead className={isDark ? 'bg-night-header' : 'bg-brand-100'}>
                    <tr>
                      <th className={`px-3 py-3 sticky top-0 ${isDark ? 'text-brand-200' : 'text-brand-900'}`}>
                        <span className="sr-only">HubSpot</span>
                      </th>
                      {searchProperties.map((prop, i) => (
                        <th key={prop} {...columnDragProps(i)} title="Drag to reorder"
                          className={`relative px-6 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 cursor-move select-none
                          ${dragOverColIndex === i ? (isDark ? 'bg-brand-800' : 'bg-brand-200') : ''}
                          ${isDark ? 'text-brand-200' : 'text-brand-900'}`}>
                          <span ref={el => { headerLabelRefs.current[prop] = el; }} className="inline-block max-w-full truncate align-bottom">
                            {propertyLabels[prop] || prop}
                          </span>
                          {i < searchProperties.length - 1 && (
                            <span
                              onPointerDown={startResize(i)}
                              onClick={e => e.stopPropagation()}
                              draggable={false}
                              onDragStart={e => e.preventDefault()}
                              title="Drag to resize"
                              className="absolute top-0 right-0 z-10 flex h-full w-4 cursor-col-resize items-center justify-end pr-1 touch-none"
                            >
                              <span aria-hidden className={`text-sm leading-none ${isDark ? 'text-gray-500/40' : 'text-gray-400/40'}`}>|</span>
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {results.map((result, idx) => (
                      <tr key={idx} className={`transition-colors duration-150
                        ${isDark
                          ? 'hover:bg-gray-700 text-gray-300'
                          : 'hover:bg-gray-50 text-gray-900'}`}>
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          {renderHubspotBadge(result.hubspot_record_id)}
                        </td>
                        {searchProperties.map(prop => (
                          <td key={prop} className="px-6 py-4 text-sm truncate">
                            {renderCellValue(prop, result[prop])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className={`mt-4 px-4 py-3 flex items-center justify-between ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <div className="text-sm">
                    {total > 0 && (
                      <span>
                        Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} of {total} results
                      </span>
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                        className={`p-2 rounded-lg transition-colors
                          ${isDark
                            ? 'hover:bg-night-header disabled:text-gray-600'
                            : 'hover:bg-gray-100 disabled:text-gray-300'}
                          disabled:cursor-not-allowed`}
                      >
                        <ChevronLeft size={20} />
                      </button>

                      <span className="px-4 py-2">
                        Page {page} of {totalPages}
                      </span>

                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages || loading}
                        className={`p-2 rounded-lg transition-colors
                          ${isDark
                            ? 'hover:bg-night-header disabled:text-gray-600'
                            : 'hover:bg-gray-100 disabled:text-gray-300'}
                          disabled:cursor-not-allowed`}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {!bulkResults && query.length >= 3 && !loading && results.length === 0 && !error &&
          query === currentSearched && (
          <div className={`mt-6 p-4 text-center rounded-lg shadow
            ${isDark
              ? 'bg-night-raised text-brand-200'
              : 'bg-white text-brand-800'}`}
          >
            No results found
          </div>
        )}

        {/* Modals */}
        <PropertyModal
          isOpen={isPropertyModalOpen}
          onClose={() => setIsPropertyModalOpen(false)}
          onSearch={handleModalSearch}
          searchProperties={searchProperties}
          allProperties={allProperties}
          isDark={isDark}
        />

        <HelpModal
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          isDark={isDark}
        />

        <BulkSearchModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          onResults={setBulkResults}
          selectedProperties={searchProperties}
          isDark={isDark}
          authFetch={authFetch}
        />
      </div>
    </div>
  );
};

export default SearchApp;